/**
 * Peticiones a endpoints de Plane (/chronus-plane/...) o integración con timeout largo
 * y manejo de 504 (Gateway Timeout) con reintento opcional.
 * Guía: Timeout y CORS para frontend.
 */
import axios from 'axios'

const PLANE_TIMEOUT_MS = 180000 // 180 segundos (3 min) - endpoint todolist-group puede tardar mucho

// Interceptor global: asegurar timeout 180s para TODAS las peticiones a chronus-plane.
// Evita que ninguna llamada se quede sin timeout y falle con (canceled).
let _interceptorId = null
function setupPlaneTimeoutInterceptor () {
  if (_interceptorId != null) return
  _interceptorId = axios.interceptors.request.use((config) => {
    const url = String(config.url || '')
    const baseURL = String(config.baseURL || '')
    const fullUrl = (baseURL + url).toLowerCase()
    if (fullUrl.includes('chronus-plane')) {
      config.timeout = config.timeout ?? PLANE_TIMEOUT_MS
    }
    return config
  })
}
setupPlaneTimeoutInterceptor()
const RETRY_DELAY_MS = 2500   // 2.5 s antes de reintentar en 504
const MAX_RETRIES_504 = 1     // un solo reintento ante 504

/**
 * Ejecuta una petición con timeout 180s. Si la respuesta es 504, reintenta una vez
 * tras RETRY_DELAY_MS y luego rechaza con un error que indica 504.
 *
 * @param {object} config - Configuración de axios (url, method, data, etc.)
 * @returns {Promise} - Promesa que resuelve con la respuesta de axios o rechaza con error
 */
export function planeRequest (config) {
  const options = {
    ...config,
    timeout: config.timeout != null ? config.timeout : PLANE_TIMEOUT_MS
  }

  function doRequest () {
    return axios(options)
  }

  function to504Error (err) {
    const e = err instanceof Error ? err : new Error(err && err.message ? err.message : 'Gateway Timeout')
    e.is504 = true
    e.message504 = true
    if (err && err.response) e.response = err.response
    return e
  }

  return doRequest().catch((err) => {
    const is504 = err.response && err.response.status === 504
    if (is504 && MAX_RETRIES_504 > 0) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          axios(options)
            .then(resolve)
            .catch((retryErr) => {
              const retry504 = retryErr.response && retryErr.response.status === 504
              reject(retry504 ? to504Error(retryErr) : retryErr)
            })
        }, RETRY_DELAY_MS)
      })
    }
    if (is504) {
      throw to504Error(err)
    }
    throw err
  })
}

/** Mensaje recomendado para mostrar al usuario ante 504 */
export const MESSAGE_504 = 'El servidor tardó demasiado en responder. Por favor, inténtalo de nuevo en unos segundos.'

/** Timeout por defecto para usar en otras configuraciones */
export const PLANE_TIMEOUT = PLANE_TIMEOUT_MS
